import { z } from "zod";

const STORAGE_KEY = "ferrivia.dismissed-ctas";
const CACHE_VERSION = 1;

/** A one-time CTA the client has already acted on. One string per surface,
    listed here rather than free-form so a typo can't silently create a CTA
    that never reappears and never gets cleaned up. */
export type DismissibleCta = "home.latestResult.allResults";

// Non-clinical app state only: which nudge has been tapped. No value, no
// analyte, no date, nothing measured — the same class of data as
// cachedSession's `onboardingComplete`, kept in its own key rather than
// widened into that schema, which is deliberately a session-bootstrap
// payload. Purged alongside it — see offline/purge.ts.
const dismissedCtasSchema = z.object({
  version: z.literal(CACHE_VERSION),
  // Namespacing is a correctness measure, not a security boundary (same
  // origin, same localStorage): it stops one person's dismissals from
  // hiding a CTA for the next person to sign in on a shared device.
  userId: z.string(),
  keys: z.array(z.string()),
});

function read(userId: string): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = dismissedCtasSchema.safeParse(JSON.parse(raw));
    if (!parsed.success || parsed.data.userId !== userId) return [];
    return parsed.data.keys;
  } catch {
    return [];
  }
}

export function isCtaDismissed(userId: string, key: DismissibleCta): boolean {
  return read(userId).includes(key);
}

export function dismissCta(userId: string, key: DismissibleCta): void {
  const keys = read(userId);
  if (keys.includes(key)) return;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ version: CACHE_VERSION, userId, keys: [...keys, key] } satisfies z.infer<typeof dismissedCtasSchema>),
    );
  } catch {
    // localStorage unavailable — the CTA simply reappears next launch,
    // which is the safe direction to fail in for a nudge.
  }
}

export function clearDismissedCtas(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // see dismissCta
  }
}
